/**
 * What the form asks, in what order, and which field cannot be answered yet.
 *
 * **Variant D has no steps, and this is what replaced them.** `assignSteps.ts` held the step list,
 * the position and where a vanished step landed the trainer; the mockup (`#d/assign/*`) puts all
 * four answers on one surface, so there is no position to hold and nothing can vanish underneath
 * anybody. What is left of that module's job is the part that was never about steps: a plan's
 * scheduling mode decides which questions EXIST, and the backend decides in what order they can be
 * answered.
 *
 * ── Three surfaces, not two ──────────────────────────────────────────────────────────────────
 * `pending` is its own answer and not a `dated` form with nothing in it. The plan read has not
 * landed, so which questions the assignment has is unknown — and a form that drew the dated fields
 * and then removed three of them when a self-paced plan finished loading is exactly the movement
 * `CLAUDE.md`'s loading policy forbids. The dialog reserves the box and draws a skeleton in it.
 *
 * ── A field that cannot be answered yet STAYS, disabled, with the reason ─────────────────────
 * View ADR §12. A field that appears when the one above it is answered moves everything below it,
 * and a trainer who cannot see the question cannot plan the four answers. So every field is on
 * screen from the first frame, `locked` carries WHY, and the control stays focusable so the reason
 * stays reachable — the whole point of `aria-disabled` over `disabled`.
 *
 * ── The order is the backend's, not a preference ─────────────────────────────────────────────
 * Client → delivery mode → times → start date. The times sit between
 * the mode and the date because that is where the mock puts them, and they exist at all only for
 * Stationary (`AssignmentScheduleInputValidation.cs`): `stationaryTimeSlots` reads the PLAN's
 * programmed weekdays.
 *
 * ── A question that does not exist is not drawn ──────────────────────────────────────────────
 * Owner decision, 22 September 2026: for a Remote assignment the hours block is ABSENT, not locked.
 * It used to be drawn greyed out so the trainer switching delivery could see what changes; a remote
 * plan books no hours at all, so what that actually showed was a question with no answer. The form
 * pane GROWS when Stacjonarnie is chosen and the owner accepts that — what the no-movement policy
 * owes here is that nothing ABOVE the block moves, and nothing does.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. Reasons leave as tokens.
 */

import type { PlanOrigin, PlanRecipient } from '~/entities/plan-draft';
import type { TrainWithMeModulesPlansDtosRequestsPostPlanTemplateRequest as PostPlanTemplateRequest } from '~/shared/api/generated/model';

import type { ScheduleTree } from './assignSchedule';

/**
 * What the dialog was opened on — and therefore what it already knows.
 *
 * · `stored` — a library entry the builder has a row for. The dialog reads it and asks who.
 * · `unsaved` — a plan the builder holds that was never stored, built FOR a client (the client
 *   detail's „Nowy plan"). The dialog carries the builder's tree, snapshotted as the exact POST
 *   body when it opened, and the client is already known.
 */
export type AssignEntry =
  | { readonly kind: 'stored'; readonly planId: number }
  | {
      readonly kind: 'unsaved';
      readonly plan: PostPlanTemplateRequest;
      readonly origin: PlanOrigin;
      readonly recipient: PlanRecipient;
    };

/** Which form this assignment has. */
export type AssignSurface = 'pending' | 'dated' | 'selfPaced';

/**
 * The surface, from the plan the dialog is actually going to assign.
 *
 * `null` is a read that has not landed: the questions are not known yet, and the form draws the
 * reserve.
 */
export function assignSurface(tree: ScheduleTree | null): AssignSurface {
  if (tree === null) return 'pending';
  return tree.schedulingMode === 'SelfPaced' ? 'selfPaced' : 'dated';
}

/** Every field the form can hold, in the order it draws them. */
export type AssignField = 'client' | 'mode' | 'times' | 'start';

export const ASSIGN_FIELD_ORDER: readonly AssignField[] = ['client', 'mode', 'times', 'start'];

/** Why a field is on screen but cannot be answered. Worded by `assignRefusalCopy`. */
export type FieldLock = 'noClient';

/**
 * A field's state.
 *
 * `absent` is not "locked with an empty reason": a self-paced assignment does not HAVE a start
 * date, and drawing one greyed out would tell the trainer a question exists that never will
 * (R11, R13). It is not rendered at all.
 */
export type FieldGate =
  | { readonly state: 'open' }
  | { readonly state: 'absent' }
  | { readonly state: 'locked'; readonly reason: FieldLock };

const OPEN: FieldGate = { state: 'open' };
const ABSENT: FieldGate = { state: 'absent' };

/** What the gate has to judge against. */
export interface FormState {
  readonly surface: AssignSurface;
  /** Whether the dialog opened already knowing the client — an unsaved plan built for one. */
  readonly knowsClient: boolean;
  readonly hasClient: boolean;
  readonly stationary: boolean;
  /** Whether this plan programmes any weekday at all — an empty plan asks for no hours. */
  readonly hasTimeSlots: boolean;
}

/**
 * One field's state. Every field, every surface — the caller renders the answer and decides nothing.
 */
export function fieldGate(field: AssignField, state: FormState): FieldGate {
  const { surface, knowsClient, hasClient, stationary, hasTimeSlots } = state;

  if (field === 'client') {
    // An unsaved plan was built FOR a client, so the dialog opens already knowing who. It still
    // NAMES them — the echo in the head — it just does not offer a list whose only possible answer
    // is the one already given.
    return knowsClient ? ABSENT : OPEN;
  }

  // Everything below is a scheduling input, and a self-paced assignment has none of them: the
  // client and the plan, and nothing else (R11 + R13).
  if (surface !== 'dated') return ABSENT;

  if (field === 'times') {
    // ABSENT for Remote, not locked (owner, 22 September 2026). A remote assignment books no hours
    // at all, so a greyed-out row of time inputs is a question that does not exist being drawn as
    // one that cannot be answered yet. The pane grows when Stacjonarnie is chosen and nothing ABOVE
    // this field moves, which is the part of the no-movement policy that is actually owed here.
    if (!stationary) return ABSENT;
    return hasTimeSlots ? OPEN : ABSENT;
  }

  // The mode and the start date both describe how ONE person trains, so neither can be answered
  // before there is a person. The mock draws them inactive with the reason under them.
  return hasClient ? OPEN : { state: 'locked', reason: 'noClient' };
}
